import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
// @mui
import { styled, useTheme } from '@mui/material/styles';
import { Box, ButtonBase, Drawer, MenuItem, Stack, Tooltip, Typography, ListItemText } from '@mui/material';
// hooks
import useResponsive from '../../../hooks/useResponsive';
import useCollapseDrawer from '../../../hooks/useCollapseDrawer';
import useAuth from '../../../hooks/useAuth';
import useWhatsappUnreadCount from '../../../hooks/useWhatsappUnreadCount';
import {isPaidFeatureAvailable} from '../../../utils/planEntitlements';
// utils
import cssStyles from '../../../utils/cssStyles';
// config
import { NAVBAR } from '../../../config';
// components
import Logo from '../../../components/Logo';
import Iconify from '../../../components/Iconify';
import Label from '../../../components/Label';
import MenuPopover from '../../../components/MenuPopover';
import Scrollbar from '../../../components/Scrollbar';
import { NavSectionVertical } from '../../../components/nav-section';
//
import navConfig from './NavConfig';
import {ownerNavigation} from '../../../utils/ownerAnalytics';
import CollapseButton from './CollapseButton';

// ----------------------------------------------------------------------

const RootStyle = styled('div')(({ theme }) => ({
  [theme.breakpoints.up('lg')]: {
    flexShrink: 0,
    transition: theme.transitions.create('width', {
      duration: theme.transitions.duration.shorter,
    }),
  },
}));

// ----------------------------------------------------------------------

NavbarVertical.propTypes = {
  isOpenSidebar: PropTypes.bool,
  onCloseSidebar: PropTypes.func,
};

export default function NavbarVertical({ isOpenSidebar, onCloseSidebar }) {
  const theme = useTheme();
  const { user, workspace, workspaces = [], selectWorkspace, planEntitlements } = useAuth();
  const whatsappAvailable = isPaidFeatureAvailable(planEntitlements, 'whatsapp');
  const unreadCount = useWhatsappUnreadCount(whatsappAvailable ? workspace?.id : null, user?.id);
  const [workspaceMenuAnchor, setWorkspaceMenuAnchor] = useState(null);
  const renderedNavConfig = ownerNavigation(navConfig, user).map((section) => ({
    ...section,
    items: section.items.map((item) => item.title === 'whatsapp' && unreadCount > 0
      ? {...item, info: <Label color="error">{unreadCount}</Label>}
      : item),
  }));

  const { pathname } = useLocation();

  const isDesktop = useResponsive('up', 'lg');

  const { isCollapse, collapseClick, collapseHover, onToggleCollapse, onHoverEnter, onHoverLeave } =
    useCollapseDrawer();

  useEffect(() => {
    if (isOpenSidebar) {
      onCloseSidebar();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  const renderContent = (
    <Scrollbar
      sx={{
        height: 1,
        '& .simplebar-content': { height: 1, display: 'flex', flexDirection: 'column' },
      }}
    >
      <Stack
        spacing={3}
        sx={{
          pt: 3,
          pb: 2,
          px: 2.5,
          flexShrink: 0,
          ...(isCollapse && { alignItems: 'center' }),
        }}
      >
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Logo sx={isCollapse ? {width: 24, ml: 1} : {width: 100, ml: 4}} isCollapse={isCollapse} />

          {isDesktop && !isCollapse && (
            <CollapseButton onToggleCollapse={onToggleCollapse} collapseClick={collapseClick} />
          )}
        </Stack>

      </Stack>

      <NavSectionVertical navConfig={renderedNavConfig} isCollapse={isCollapse} />

      <Box sx={{ flexGrow: 1 }} />

      <Box sx={{ px: isCollapse ? 1.25 : 2.5, pb: 2, flexShrink: 0 }}>
        <Tooltip title={isCollapse ? workspace?.title || "Espaço de trabalho" : ''} placement="right">
          <ButtonBase
            id="workspace-switcher-button"
            aria-label={`Trocar espaço de trabalho. Atual: ${workspace?.title || 'nenhum'}`}
            aria-haspopup="menu"
            aria-expanded={Boolean(workspaceMenuAnchor)}
            data-tut="workspace-switcher"
            disabled={!workspaces.length}
            onClick={(event) => setWorkspaceMenuAnchor(event.currentTarget)}
            sx={{
              width: 1,
              minHeight: isCollapse ? 48 : 64,
              p: isCollapse ? 0.75 : 1.25,
              gap: 1.25,
              justifyContent: 'flex-start',
              textAlign: 'left',
              borderRadius: 1.5,
              border: `1px solid ${theme.palette.grey[500_12]}`,
              bgcolor: workspaceMenuAnchor ? 'action.selected' : theme.palette.grey[500_8],
              transition: theme.transitions.create(['background-color', 'transform']),
              '&:hover': { bgcolor: 'action.hover' },
              '&:active': { transform: 'scale(0.98)' },
              '&.Mui-focusVisible': { outline: `2px solid ${theme.palette.primary.main}`, outlineOffset: 2 },
            }}
          >
            <Box
              sx={{
                width: 34,
                height: 34,
                flexShrink: 0,
                display: 'grid',
                placeItems: 'center',
                borderRadius: 1,
                color: 'primary.dark',
                bgcolor: 'primary.lighter',
              }}
            >
              <Iconify icon="eva:cube-outline" width={19} height={19}/>
            </Box>

            {!isCollapse ? (
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography variant="caption" color="text.secondary" noWrap>
                  Espaço atual
                </Typography>
                <Typography variant="subtitle2" noWrap>
                  {workspace?.title || "Nenhum espaço selecionado"}
                </Typography>
              </Box>
            ) : null}

            {!isCollapse ? (
              <Iconify
                icon="eva:chevron-down-fill"
                width={18}
                height={18}
                sx={{
                  flexShrink: 0,
                  color: 'text.secondary',
                  transform: workspaceMenuAnchor ? 'rotate(180deg)' : 'none',
                  transition: theme.transitions.create('transform'),
                }}
              />
            ) : null}
          </ButtonBase>
        </Tooltip>

        <MenuPopover
          open={Boolean(workspaceMenuAnchor)}
          anchorEl={workspaceMenuAnchor}
          onClose={() => setWorkspaceMenuAnchor(null)}
          arrow="bottom-right"
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
          transformOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          sx={{
            p: 1,
            mb: 1.25,
            width: 260,
            '& .MuiMenuItem-root': {
              px: 1,
              py: 1,
              minHeight: 54,
              borderRadius: 0.75,
            },
          }}
        >
          <Box sx={{ px: 1, pt: 0.5, pb: 1 }}>
            <Typography variant="subtitle2">Selecionar espaço de trabalho</Typography>
            <Typography variant="caption" color="text.secondary">
              O conteúdo do painel acompanha esta seleção.
            </Typography>
          </Box>

          <Stack role="menu" aria-labelledby="workspace-switcher-button" spacing={0.5}>
            {workspaces.map((item) => {
              const isSelected = item.id === workspace?.id;
              return (
                <MenuItem
                  key={item.id}
                  role="menuitemradio"
                  aria-checked={isSelected}
                  selected={isSelected}
                  onClick={() => {
                    selectWorkspace(item);
                    setWorkspaceMenuAnchor(null);
                    if (!isDesktop) onCloseSidebar();
                  }}
                >
                  <Box
                    sx={{
                      width: 34,
                      height: 34,
                      mr: 1.25,
                      flexShrink: 0,
                      display: 'grid',
                      placeItems: 'center',
                      borderRadius: 1,
                      color: isSelected ? 'primary.dark' : 'text.secondary',
                      bgcolor: isSelected ? 'primary.lighter' : theme.palette.grey[500_12],
                    }}
                  >
                    <Iconify icon="eva:cube-outline" width={18} height={18}/>
                  </Box>
                  <ListItemText
                    primary={item.title}
                    secondary={item.isAdmin ? 'Administrador' : 'Membro'}
                    primaryTypographyProps={{ variant: 'subtitle2', noWrap: true }}
                    secondaryTypographyProps={{ variant: 'caption', noWrap: true }}
                  />
                  {isSelected ? (
                    <Iconify icon="eva:checkmark-circle-2-fill" width={20} height={20} sx={{ ml: 1, color: 'primary.main' }}/>
                  ) : null}
                </MenuItem>
              );
            })}
          </Stack>
        </MenuPopover>
      </Box>

    </Scrollbar>
  );

  return (
    <RootStyle
      sx={{
        width: {
          lg: isCollapse ? NAVBAR.DASHBOARD_COLLAPSE_WIDTH : NAVBAR.DASHBOARD_WIDTH,
        },
        ...(collapseClick && {
          position: 'absolute',
        }),
      }}
    >
      {!isDesktop && (
        <Drawer open={isOpenSidebar} onClose={onCloseSidebar} PaperProps={{ sx: { width: NAVBAR.DASHBOARD_WIDTH } }}>
          {renderContent}
        </Drawer>
      )}

      {isDesktop && (
        <Drawer
          open
          variant="persistent"
          onMouseEnter={onHoverEnter}
          onMouseLeave={onHoverLeave}
          PaperProps={{
            sx: {
              width: NAVBAR.DASHBOARD_WIDTH,
              borderRightStyle: 'dashed',
              bgcolor: 'background.default',
              transition: (theme) =>
                theme.transitions.create('width', {
                  duration: theme.transitions.duration.standard,
                }),
              ...(isCollapse && {
                width: NAVBAR.DASHBOARD_COLLAPSE_WIDTH,
              }),
              ...(collapseHover && {
                ...cssStyles(theme).bgBlur(),
                boxShadow: (theme) => theme.customShadows.z24,
              }),
            },
          }}
        >
          {renderContent}
        </Drawer>
      )}
    </RootStyle>
  );
}
